/**
 * The browser's notifications banner above the shell (design: huddle ring on the web, Web.dc.html), the same strip as
 * the desktop app's: "Turn on notifications…" while the browser hasn't been asked (Turn on asks it, from the click:
 * NotificationsViewModel.enable, or the bare permission where web push isn't offered), and how to allow them from the
 * address bar's lock icon once it said no (a page can't open the browser's settings). × hides it for this session.
 * Which one is ringAlert.ts `webNotifyBanner`; the permission is re-read when it changes in the browser.
 *
 * Desktop app (design: Blocked.dc.html): "macOS is blocking Bundle's notifications." while the OS says no
 * (NotificationsViewModel `blockedNotice`), with Open System Settings straight to Bundle's page there. It folds away by
 * itself once the OS allows them (re-read on focus); × hides it for a week, and the Settings › Notifications dot stays.
 */
import { useEffect, useState } from "react";
import { Banner, Reveal, useT, useViewModel } from "@bundle/ui";
import { isDesktop } from "../platform/desktop.ts";
import { webNotifyBanner } from "../platform/ringAlert.ts";
import { usePlatform } from "./PlatformContext.ts";

const CLOSED_KEY = "bundle.notifyBanner.closed";

function readClosed(): boolean {
  try {
    return sessionStorage.getItem(CLOSED_KEY) === "1";
  } catch {
    return false;
  }
}

function currentPermission(): NotificationPermission | undefined {
  return "Notification" in window ? Notification.permission : undefined;
}

export function NotificationsBanner() {
  return isDesktop() ? <DesktopBlockedBanner /> : <WebNotificationsBanner />;
}

function DesktopBlockedBanner() {
  const t = useT();
  const notifications = usePlatform().notifications;
  const state = useViewModel(notifications);
  const os = state.os;
  return (
    <Reveal shown={os !== undefined && state.blockedNotice === true && state.canOpenSettings === true} testID="notifications-blocked-reveal">
      {os === undefined ? null : (
        <Banner
          tone="warning"
          icon="bellOff"
          placement="edge"
          title={t(`notifications.blocked.title.${os}`)}
          inlineTitle
          message={t("notifications.blocked.body")}
          action={{ label: t(`notifications.blocked.open.${os}`), variant: "primary", onPress: () => notifications.openSettings(), testID: "notifications-blocked-open" }}
          onDismiss={() => notifications.dismissBlocked()}
          dismissLabel={t("notifications.blocked.dismiss")}
          testID="notifications-blocked-banner"
        />
      )}
    </Reveal>
  );
}

function WebNotificationsBanner() {
  const t = useT();
  const notifications = usePlatform().notifications;
  const status = useViewModel(notifications).status;
  const [permission, setPermission] = useState(currentPermission);
  const [closed, setClosed] = useState(readClosed);

  // Allowed or blocked from the address bar: the banner follows, and the view model re-reads its status.
  useEffect(() => {
    let off: (() => void) | undefined;
    const changed = () => {
      setPermission(currentPermission());
      notifications.refresh();
    };
    void navigator.permissions
      ?.query({ name: "notifications" })
      .then((p) => {
        p.addEventListener("change", changed);
        off = () => p.removeEventListener("change", changed);
      })
      .catch(() => undefined);
    return () => off?.();
  }, [notifications]);

  const kind = webNotifyBanner(status, permission);
  const shown = kind !== "none" && !closed;
  const [last, setLast] = useState(kind);
  // Keeps the text while the banner folds away.
  if (kind !== "none" && kind !== last) setLast(kind);

  const turnOn = () => {
    if (status === "off") {
      void notifications.enable();
      return;
    }
    // No web push here (unsupported/unconfigured build): local notifications only need the permission.
    void Notification.requestPermission().then(setPermission, () => undefined);
  };
  const close = () => {
    setClosed(true);
    try {
      sessionStorage.setItem(CLOSED_KEY, "1");
    } catch {
      // Private mode: this page load only.
    }
  };

  return (
    <Reveal shown={shown} testID="notifications-banner-reveal">
      {last === "blocked" ? (
        <Banner tone="warning" icon="bell" placement="edge" message={t("notifications.web.blockedBody")} onDismiss={close} testID="notifications-banner-blocked" />
      ) : (
        <Banner
          tone="warning"
          icon="bell"
          placement="edge"
          message={t("notifications.web.offBody")}
          action={{ label: t("notifications.web.turnOn"), variant: "primary", onPress: turnOn, testID: "notifications-banner-turn-on" }}
          onDismiss={close}
          testID="notifications-banner-off"
        />
      )}
    </Reveal>
  );
}
